/**
 * Etiquettes d'entree (« Tags » de 1Password et de KeePassXC) : un classement
 * transversal aux dossiers -- une entree n'a qu'un dossier, mais peut etre a la
 * fois « travail », « banque » et « a changer ». La barre laterale liste les
 * etiquettes du coffre, un clic filtre la liste, la recherche les trouve.
 *
 * Les etiquettes vivent DANS les secrets chiffres (`secrets.tags`), comme
 * `archived` et `reprompt` : le serveur ne sait ni quelles etiquettes existent,
 * ni lesquelles portent les entrees. Module pur, zero E/S.
 *
 * Etiquettes imbriquees (« Nested tags » de 1Password) : « Travail/Clients » est
 * rangee sous « Travail ». La vue d'une etiquette montre aussi les entrees de ses
 * sous-etiquettes ; renommer ou supprimer une etiquette emporte ses
 * sous-etiquettes. Un parent que personne ne porte apparait quand meme dans la
 * barre laterale.
 */
import type { EntrySecrets } from '@klavox/crypto-core';
import { withSiblingRank, type VisibleFolderRow } from './folder-tree.js';

/** Etiquettes conservees par entree (les suivantes sont abandonnees). */
export const MAX_ENTRY_TAGS = 32;

/** Longueur maximale d'une etiquette, en caracteres. */
export const MAX_TAG_LENGTH = 64;

/** Separateur des niveaux d'une etiquette imbriquee (« Travail/Clients »), celui de 1Password. */
export const TAG_PATH_SEPARATOR = '/';

/**
 * Etiquette de la barre laterale : chemin complet (`tag`), dernier niveau a
 * afficher (`label`), niveau (`depth`, 0 a la racine) et nombre d'entrees qui
 * la portent, elle ou l'une de ses sous-etiquettes (une entree comptee une fois).
 */
export interface TagCount {
  readonly tag: string;
  readonly count: number;
  readonly label: string;
  readonly depth: number;
}

/**
 * Etiquette nettoyee : `#` initial retire (habitude des tags), espaces resserres,
 * virgule et point-virgule changes en espace (ce sont les separateurs de la
 * saisie : une etiquette importee qui en contient doit survivre a une edition),
 * niveaux vides et espaces autour de `/` retires (« Travail / Clients » et
 * « Travail//Clients » sont « Travail/Clients »), tronquee a `MAX_TAG_LENGTH`.
 * Chaine vide = pas d'etiquette.
 */
export function normalizeTag(raw: string): string {
  const cleaned = raw
    .normalize('NFC')
    .replace(/[,;]/gu, ' ')
    .replace(/\s+/gu, ' ')
    .trim()
    .replace(/^#+\s*/u, '')
    .replace(/\s*\/+\s*/gu, TAG_PATH_SEPARATOR);
  return trimSeparators([...trimSeparators(cleaned)].slice(0, MAX_TAG_LENGTH).join(''));
}

function trimSeparators(tag: string): string {
  return tag.replace(/^[\s/]+|[\s/]+$/gu, '');
}

/** Niveaux d'une etiquette (« Travail/Clients » : « Travail », « Clients »). */
export function tagPath(tag: string): string[] {
  return tag.split(TAG_PATH_SEPARATOR);
}

/** `tag` est-elle `parent` ou l'une de ses sous-etiquettes (casse ignoree) ? « Travaux » n'est pas sous « Travail ». */
export function isWithinTag(tag: string, parent: string): boolean {
  const key = tagKey(tag);
  const parentKey = tagKey(parent);
  return parentKey.length > 0 && (key === parentKey || key.startsWith(parentKey + TAG_PATH_SEPARATOR));
}

/** L'entree porte-t-elle cette etiquette ou l'une de ses sous-etiquettes ? C'est le filtre de la vue d'une etiquette. */
export function hasTagWithin(secrets: EntrySecrets, tag: string): boolean {
  return readTags(secrets).some((candidate) => isWithinTag(candidate, tag));
}

/** Cle de comparaison : « Banque » et « banque » sont la meme etiquette. */
export function tagKey(tag: string): string {
  return normalizeTag(tag).toLocaleLowerCase('fr');
}

/** Liste nettoyee : sans vide, sans doublon (la premiere graphie gagne), plafonnee. Valeurs non textuelles ignorees. */
export function normalizeTags(raw: readonly unknown[]): string[] {
  const seen = new Set<string>();
  const tags: string[] = [];
  for (const value of raw) {
    if (typeof value !== 'string') continue;
    const tag = normalizeTag(value);
    const key = tag.toLocaleLowerCase('fr');
    if (tag.length === 0 || seen.has(key)) continue;
    seen.add(key);
    tags.push(tag);
    if (tags.length === MAX_ENTRY_TAGS) break;
  }
  return tags;
}

/** Saisie de l'editeur ou colonne d'export : etiquettes separees par virgule, point-virgule ou retour a la ligne. */
export function parseTagList(text: string): string[] {
  return normalizeTags(text.split(/[,;\n]/u));
}

/** Saisie de l'editeur a partir des etiquettes de l'entree. */
export function formatTagList(tags: readonly string[]): string {
  return tags.join(', ');
}

/** Etiquettes de l'entree ; une valeur importee mal formee vaut « aucune ». */
export function readTags(secrets: EntrySecrets): string[] {
  const raw = secrets['tags'];
  return Array.isArray(raw) ? normalizeTags(raw) : [];
}

/** Secrets avec ces etiquettes ; rien d'autre ne change. Liste vide = cle retiree. */
export function withTags(secrets: EntrySecrets, tags: readonly string[]): EntrySecrets {
  const { tags: _tags, ...rest } = secrets;
  const normalized = normalizeTags(tags);
  return normalized.length > 0 ? { ...rest, tags: normalized } : rest;
}

/** L'entree porte-t-elle cette etiquette (sans tenir compte de la casse) ? */
export function hasTag(secrets: EntrySecrets, tag: string): boolean {
  const key = tagKey(tag);
  return key.length > 0 && readTags(secrets).some((candidate) => candidate.toLocaleLowerCase('fr') === key);
}

/**
 * Etiquettes d'un ensemble d'entrees, comptees, en arbre : chaque parent suivi de
 * ses sous-etiquettes, ordre alphabetique a chaque niveau (la graphie la plus
 * frequente l'emporte). Un parent que personne ne porte apparait quand meme ;
 * il compte les entrees de ses sous-etiquettes, chacune une fois.
 */
export function collectTags(entries: readonly { readonly secrets: EntrySecrets }[]): TagCount[] {
  const byKey = new Map<string, { count: number; spellings: Map<string, number> }>();
  for (const entry of entries) {
    // Chaque niveau porte par l'entree, une fois : « a/b » et « a/c » ne la comptent qu'une fois sous « a ».
    const covered = new Map<string, string>();
    for (const tag of readTags(entry.secrets)) {
      const path = tagPath(tag);
      for (let depth = 1; depth <= path.length; depth += 1) {
        const prefix = path.slice(0, depth).join(TAG_PATH_SEPARATOR);
        const key = prefix.toLocaleLowerCase('fr');
        if (!covered.has(key)) covered.set(key, prefix);
      }
    }
    for (const [key, prefix] of covered) {
      const bucket = byKey.get(key) ?? { count: 0, spellings: new Map<string, number>() };
      bucket.count += 1;
      bucket.spellings.set(prefix, (bucket.spellings.get(prefix) ?? 0) + 1);
      byKey.set(key, bucket);
    }
  }
  return [...byKey.values()]
    .map(({ count, spellings }) => {
      // A egalite, la premiere graphie rencontree (tri stable).
      const tag = [...spellings.entries()].sort((a, b) => b[1] - a[1])[0]![0];
      const path = tagPath(tag);
      return { tag, count, label: path[path.length - 1]!, depth: path.length - 1 };
    })
    .sort((a, b) => compareTagPaths(a.tag, b.tag));
}

/**
 * Ligne visible de l'arbre des etiquettes : `hasChildren` porte le chevron, `collapsed` le dit replie,
 * `holdsActive` signale que l'etiquette de la vue est cachee dessous.
 */
export interface VisibleTagRow extends TagCount {
  readonly hasChildren: boolean;
  readonly collapsed: boolean;
  readonly holdsActive: boolean;
  /** Rang parmi ses freres (1 = premiere) et nombre de freres, elle comprise : `aria-posinset` / `aria-setsize`. */
  readonly position: number;
  readonly setSize: number;
}

/**
 * Arbre de `collectTags` sans les sous-etiquettes d'un parent replie (`collapsed` = cles `tagKey`), comme
 * `visibleFolderTree` pour les dossiers. Replier n'est qu'un affichage : la vue du parent garde ses sous-etiquettes.
 */
export function visibleTagTree(rows: readonly TagCount[], collapsed: ReadonlySet<string>, activeTag: string | null): VisibleTagRow[] {
  const activeKey = activeTag === null ? null : tagKey(activeTag);
  const visible: Omit<VisibleTagRow, 'position' | 'setSize'>[] = [];
  for (let index = 0; index < rows.length; index += 1) {
    const row = rows[index]!;
    const hasChildren = (rows[index + 1]?.depth ?? -1) > row.depth;
    const isCollapsed = hasChildren && collapsed.has(tagKey(row.tag));
    let holdsActive = false;
    let next = index + 1;
    if (isCollapsed) {
      for (; next < rows.length && rows[next]!.depth > row.depth; next += 1) {
        if (tagKey(rows[next]!.tag) === activeKey) holdsActive = true;
      }
    }
    visible.push({ ...row, hasChildren, collapsed: isCollapsed, holdsActive });
    index = next - 1;
  }
  return withSiblingRank(visible);
}

/** Une etiquette de l'arbre vue par le clavier de l'arbre (`folderTreeKey`, recherche par frappe) : son chemin, son libelle. */
export interface TagTreeNode {
  /** Le chemin complet (« Travail/Clients ») : c'est le `data-id` de la ligne. */
  readonly id: string;
  /** Le dernier niveau, tel qu'affiche : la recherche par frappe le lit. */
  readonly label: string;
}

/**
 * Les lignes de `visibleTagTree` sous la forme que lisent le clavier et la recherche par frappe de l'arbre des dossiers
 * (`folderTreeKey`, `folderTreeTypeAhead`, `folderTreeTabStop`) : un seul motif ARIA « tree » pour les deux arbres de la
 * barre laterale, pas une copie qui divergerait.
 */
export function tagTreeNavRows(rows: readonly VisibleTagRow[]): VisibleFolderRow<TagTreeNode>[] {
  return rows.map(({ tag, label, depth, hasChildren, collapsed, holdsActive, position, setSize }) => ({
    folder: { id: tag, label },
    depth,
    hasChildren,
    collapsed,
    holdsActive,
    position,
    setSize,
  }));
}

/** Ordre de l'arbre : niveau par niveau, un parent avant ses sous-etiquettes. */
function compareTagPaths(a: string, b: string): number {
  const left = tagPath(a);
  const right = tagPath(b);
  for (let index = 0; index < Math.min(left.length, right.length); index += 1) {
    const order = left[index]!.localeCompare(right[index]!, 'fr', { sensitivity: 'base', numeric: true });
    if (order !== 0) return order;
  }
  return left.length - right.length;
}

/*
 * Gestion des etiquettes (renommer ou supprimer partout, etiqueter une
 * selection) : chaque geste est une transformation des secrets d'UNE entree,
 * appliquee par `VaultStore` a chaque entree concernee. `tagsChanged` dit si
 * l'entree est reellement modifiee : une entree intacte n'est pas reecrite
 * (pas de revision inutile a synchroniser).
 */

/** Secrets avec l'etiquette en plus ; inchanges si elle y est deja (casse ignoree), vide, ou si l'entree en porte deja `MAX_ENTRY_TAGS`. */
export function addTag(secrets: EntrySecrets, tag: string): EntrySecrets {
  const added = normalizeTag(tag);
  const tags = readTags(secrets);
  if (added.length === 0 || hasTag(secrets, added) || tags.length >= MAX_ENTRY_TAGS) return secrets;
  return withTags(secrets, [...tags, added]);
}

/** Secrets sans l'etiquette (casse ignoree) ; inchanges si l'entree ne la porte pas. */
export function removeTag(secrets: EntrySecrets, tag: string): EntrySecrets {
  if (!hasTag(secrets, tag)) return secrets;
  const key = tagKey(tag);
  return withTags(
    secrets,
    readTags(secrets).filter((candidate) => candidate.toLocaleLowerCase('fr') !== key),
  );
}

/**
 * Secrets ou `from` devient `to`, a sa place dans la liste, sous-etiquettes
 * comprises (« Travail/Clients » devient « Pro/Clients » quand « Travail »
 * devient « Pro »). Si l'entree porte deja le nouveau nom, les deux fusionnent
 * (la premiere position gagne). « banque » vers « Banque » est un vrai
 * renommage : la graphie change. Inchanges si l'entree ne porte ni `from` ni
 * l'une de ses sous-etiquettes, ou si `to` est vide.
 */
export function renameTag(secrets: EntrySecrets, from: string, to: string): EntrySecrets {
  const renamed = normalizeTag(to);
  if (renamed.length === 0 || !hasTagWithin(secrets, from)) return secrets;
  const depth = tagPath(normalizeTag(from)).length;
  return withTags(
    secrets,
    readTags(secrets).map((candidate) =>
      isWithinTag(candidate, from) ? [renamed, ...tagPath(candidate).slice(depth)].join(TAG_PATH_SEPARATOR) : candidate,
    ),
  );
}

/**
 * Glisser l'etiquette `dragged` sur `parent` (`null` = racine) dans la barre laterale : son nouveau nom, son dernier
 * niveau sous `parent` (« Clients » sur « Pro » donne « Pro/Clients », « Travail/Clients » a la racine donne
 * « Clients ») -- c'est un renommage, sous-etiquettes comprises. `null` quand rien ne doit bouger : sur elle-meme
 * ou l'une de ses sous-etiquettes (cycle), deja a cette place, ou nom trop long (tronque, il perdrait un niveau).
 * Un nom deja porte n'est pas refuse : les deux fusionnent, comme « Renommer ».
 */
export function tagDropRename(dragged: string, parent: string | null): string | null {
  const from = normalizeTag(dragged);
  if (from.length === 0) return null;
  const leaf = tagPath(from).at(-1)!;
  if (parent !== null && (normalizeTag(parent).length === 0 || isWithinTag(parent, from))) return null;
  const to = parent === null ? leaf : `${normalizeTag(parent)}${TAG_PATH_SEPARATOR}${leaf}`;
  return to === from || normalizeTag(to) !== to ? null : to;
}

/** Secrets sans l'etiquette ni ses sous-etiquettes (« Supprimer l'etiquette » partout) ; inchanges si l'entree n'en porte aucune. */
export function removeTagWithin(secrets: EntrySecrets, tag: string): EntrySecrets {
  if (!hasTagWithin(secrets, tag)) return secrets;
  return withTags(
    secrets,
    readTags(secrets).filter((candidate) => !isWithinTag(candidate, tag)),
  );
}

/** Les etiquettes different-elles, graphie et ordre compris ? */
export function tagsChanged(before: EntrySecrets, after: EntrySecrets): boolean {
  const a = readTags(before);
  const b = readTags(after);
  return a.length !== b.length || a.some((tag, index) => tag !== b[index]);
}
